import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbAccordionItem, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BannerAppComponent } from '../../banner-app/banner-app.component';
import { AutorizationModalComponent } from '../../common/autorization-modal/autorization-modal.component';
import { FooterAppComponent } from '../../footer-app/footer-app.component';

import { getFromStorage, getSessionId, saveToStorageAsync } from '../../../helpers/storageUtils';
import { DatosGenerales } from '../../../Models/datosGenerales';
import { leadUpdate } from '../../../Models/updateLead';
import {
  emailValidator,
  exactLengthValidator,
  numericValidator,
  restrictedEmailsValidator,
  startsWithValidatorNumber,
} from '../../../motos/utils/motos-forms-utils';
import { alphabeticValidator } from '../../../motos/utils/remaining-data-form.utils';
import { DataLayerService } from '../../../services/DataLayer/data-layer.service';
import { LeadsService } from '../../../services/Leads/leads.service';
import { TrackingService } from '../../../services/tracking.service';
import { selectData } from '../../../storage/Lead/data.selectors';

@Component({
  selector: 'app-cotizacion-pausa',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FooterAppComponent,
    BannerAppComponent,
    NgbAccordionItem,
  ],
  templateUrl: './cotizacion-pausa.component.html',
  styleUrl: './cotizacion-pausa.component.scss',
})
export class CotizacionPausaComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private modalService = inject(NgbModal);
  private leadServices = inject(LeadsService);
  private trackingService = inject(TrackingService);
  private dataLayerService = inject(DataLayerService);

  public formulario: FormGroup;
  public showOnerousStyles = signal(false);
  private datosGenerales: DatosGenerales | null = null;
  private sessionId = '';
  private leadId = '';
  private codePause?: number;
  title = signal<string>('Cotización en pausa');
  description = signal<string>(`Tenemos otro producto disponible para ti. Déjanos tus<br />
            datos y nos pondremos en contacto contigo ;)`);
  descriptionMobile = signal<string>(
    'Déjanos tus datos y nos pondremos<br />en contacto contigo ;)',
  );

  private destroy$ = new Subject<void>();

  origen = getFromStorage('origen_pausa', 'session') ?? '';

  // Estados de validación para reactividad
  nameValid = false;
  nameInvalid = false;
  nameTouched = false;

  mobilePhoneValid = false;
  mobilePhoneInvalid = false;
  mobilePhoneTouched = false;

  emailValid = false;
  emailInvalid = false;
  emailTouched = false;

  globalConfigOneroso = signal(false);

  constructor() {
    this.formulario = this.fb.group({
      name: ['', alphabeticValidator()],
      mobile_phone: [
        '',
        [
          Validators.required,
          numericValidator(),
          exactLengthValidator(10),
          startsWithValidatorNumber(300, 350),
        ],
      ],
      email: ['', [Validators.required, emailValidator(), restrictedEmailsValidator()]],
      termsAndConditions: [false, [Validators.requiredTrue]], // Por defecto no está autorizado.
    });
  }

  ngOnInit(): void {
    const OnerousKeys = [
      'Accesorios',
      'Prenda',
      'PrendaAccesorios',
      '0KMPrenda',
      '0KMAccesorios',
      'full',
    ];
    if (OnerousKeys.includes(String(this.origen))) {
      this.globalConfigOneroso.set(true);
    } else {
      this.globalConfigOneroso.set(false);
    }
    this.configurarMensajes();

    this.sessionId = getSessionId().toString();
    this.loadInitialData();
    this.trackPageView();
    this.codePause = this.trackingService.getcodePause();
  }

  private configurarMensajes(): void {
    switch (this.origen) {
      case 'consulta-runt':
        this.showOnerousStyles.set(false);
        this.title.set('La información del RUNT está desactualizada');
        this.description.set('Déjanos tus datos y nos pondremos en contacto contigo;) ');
        break;

      case 'error_salvamento':
        this.showOnerousStyles.set(false);
        this.title.set('Tenemos otro producto disponible para ti');
        this.description.set('Déjanos tus datos y nos pondremos en contacto contigo.');
        break;

      case 'Prenda':
      case '0KMPrenda':
      case 'PrendaAccesorios':
      case 'full':
        this.showOnerousStyles.set(true);
        this.title.set('Te acompañamos en este paso');
        this.description.set(
          'Por tener prenda con el banco, no puedes finalizar la compra en línea. Pero tranquilo: <strong>uno de nuestros asesores te llamará muy pronto para guiarte</strong> y asegurarnos de que todo sea fácil y rápido.',
        );
        break;

      case 'Accesorios':
        this.showOnerousStyles.set(false);
        this.title.set('Este último paso lo hacemos juntos');
        this.description.set(
          'Dependiendo de tu caso, podrías necesitar una inspección. Si es así, te enviaremos un correo con el paso a paso para agendarla de forma virtual. <br><br> Si no la necesitas, uno de nuestros asesores te llamará para ayudarte a finalizar la compra.',
        );
        break;

      default:
        this.showOnerousStyles.set(false);
        this.title.set('Cotización en pausa');
        this.description.set(
          'Tenemos otro producto disponible para ti. Déjanos tus datos y nos pondremos en contacto contigo.',
        );
        break;
    }
  }

  // private configOnerosos(): void {
  //   const OnerousKeys = ['Accesorios', 'Prenda', 'PrendaAccesorios', '0KMPrenda', 'full'];

  //   const OnerousValue: any = getFromStorage('OnerousSelection', 'session') ?? '';
  //   const leadData: any = getFromStorage('UpdatedLeadData', 'session') ?? null;

  //   console.log('OnerousValue', OnerousKeys.includes(OnerousValue), OnerousValue);

  //   if (OnerousKeys.includes(OnerousValue)) {
  //     this.formulario.patchValue({ nombre: `${leadData?.firsnames}`, celular: leadData?.phone });
  //     this.title.set('Te acompañamos en este paso');
  //     this.description.set(
  //       `Por tener prenda con el banco, no puedes finalizar la compra en línea. Pero tranquilo:<b> uno de nuestros asesores te llamará muy pronto para guiarte</b> y asegurarnos de que todo sea fácil y rápido.`,
  //     );
  //     this.globalConfigOneroso.set(true);
  //   } else {
  //     this.globalConfigOneroso.set(false);
  //   }
  // }
  /**
   * Carga los datos iniciales necesarios para el componente desde el Store y los query params.
   */
  private loadInitialData(): void {
    this.store
      .select(selectData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.datosGenerales = data;
        if (data?.Lead) {
          this.formulario.patchValue({
            name: data.Lead.name,
            mobile_phone: data.Lead.mobile_phone,
          });

          // Ejecutar validación automáticamente después de cargar datos
          if (data.Lead.name) {
            this.nameTouched = true;
            this.validateField('name');
          }
          if (data.Lead.mobile_phone) {
            this.mobilePhoneTouched = true;
            this.validateField('mobile_phone');
          }
        }
        this.leadId = data?.LeadResponse?.data?.Id ?? data?.LeadResponse?.data?.lead_id ?? '';
      });
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const codAsesor = params['codigoAsesor'] || '';
      // Aquí se podría usar el codAsesor si fuera necesario.
    });
  }

  /**
   * Envía el evento de vista de página al servicio de tracking.
   */
  private trackPageView(): void {
    this.trackingService.enviarEventoCotizacionPausa('cotizacion_pausa_estado', 'Pageview');
  }

  /**
   * Procesa el envío del formulario.
   */
  public siguientePagina(): void {

    const origenPausa = getFromStorage('origen_pausa', 'session') ?? '';

    if (['Prenda', '0KMPrenda', 'PrendaAccesorios', 'full'].includes(String(origenPausa))) {
      this.dataLayerService.pushDataLayerOnerous(
        'Button',
        'moto_ecommerce_step_2.5_Fin_Flujo_Prenda',
        'Fin Flujo Prenda',
      );
    } else if (origenPausa === 'Accesorios') {
      this.dataLayerService.pushDataLayerOnerous(
        'Button',
        'moto_ecommerce_step_2.6_Fin_Flujo_Accesorios',
        'Fin Flujo Accesorios',
      );
    }

    if (this.formulario.invalid) {
      this.formulario.markAllAsTouched();
      return;
    }

    // Construimos el objeto para la actualización del lead con los valores del formulario.
    const leadUpdateData: leadUpdate = {
      lastname: this.formulario.value.name,
      phone: this.formulario.value.mobile_phone.toString(),
      email: this.formulario.value.email.toString(),
      accept_tos: this.formulario.value.termsAndConditions,
    };

    this.trackingService.enviarEventoCotizacionPausa('cotizacion_pausa_estado', 'Button', 'Enviar');

    saveToStorageAsync('ya_tenemos_tus_datos', this.codePause, 'session');

    // Llamamos al servicio para actualizar el lead.
    this.leadServices
      .actualizarLeads(leadUpdateData, this.sessionId, this.leadId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => this.leadServices.handleSuccess(resp),
        error: (error) => console.error('Error al actualizar lead:', error),
      });
  }

  /**
   * Abre el modal de la política de privacidad.
   */
  public openModal(): void {
    const modalRef = this.modalService.open(AutorizationModalComponent, {
      size: 'lg',
      centered: true, // Centra el modal verticalmente.
    });
    modalRef.componentInstance.title = 'Política de privacidad y Tratamiento de datos personales';
  }

  /**
   * Método del ciclo de vida que se ejecuta al destruir el componente.
   * Es crucial para limpiar suscripciones y evitar fugas de memoria.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Métodos de validación con reactividad
  validateField(fieldName: string): void {
    const control = this.formulario.get(fieldName);
    const isValid = control?.valid && control?.value;

    switch (fieldName) {
      case 'name':
        this.nameValid = isValid && this.nameTouched;
        this.nameInvalid = !isValid && this.nameTouched;
        break;
      case 'mobile_phone':
        this.mobilePhoneValid = isValid && this.mobilePhoneTouched;
        this.mobilePhoneInvalid = !isValid && this.mobilePhoneTouched;
        break;
      case 'email':
        this.emailValid = isValid && this.emailTouched;
        this.emailInvalid = !isValid && this.emailTouched;
        break;
    }
  }

  // Métodos para manejar el blur de cada campo
  onNameBlur(): void {
    this.nameTouched = true;
    this.validateField('name');
  }

  onMobilePhoneBlur(): void {
    this.mobilePhoneTouched = true;
    this.validateField('mobile_phone');
  }

  onEmailBlur(): void {
    this.emailTouched = true;
    this.validateField('email');
  }

  blockUnicode(event: KeyboardEvent) {
    // Bloquea ñ y Ñ
    if (event.key === 'ñ' || event.key === 'Ñ') {
      event.preventDefault();
    }
  }
}
